import { fetchPrivyAppSignerGranted } from '@/api/privy-app-signer';
import { postCustodialGasEnsure } from '@/api/custodial-gas';
import { warmPrivyTradingReadyAfterBind } from '@/utils/privy-trading-ready';
import { bindPrivyAccount } from '@/api/auth';
import { grantPrivyAppSigner } from '@/utils/privy-app-signer-grant';
import {
  getAccessToken,
  getOriginLoginType,
  persistLoginResponse,
  setWalletAddress,
  setWalletProviderId,
  setOriginLoginType,
  setLoginWalletProviderId,
} from '@/utils/auth-token';
import { noteAnalyticsLoginMethod } from '@/utils/analytics-acquisition';
import {
  createPrivyWalletLoginCredentials,
  extractPrivyEmail,
  extractPrivyTradeAddressFields,
  getPrivySessionUser,
} from '@/utils/privy';
import {
  addressesEqual,
  fetchTradeAddressFromList,
  hasCustodialTradeAddress,
  registerPrivyTradeAddress,
} from '@/utils/trade-address';

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

function normalizeOriginLoginType(value) {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase();
  if (normalized === 'EMAIL' || normalized === 'GOOGLE' || normalized === 'WALLET') {
    return normalized;
  }
  return '';
}

function shouldAttachLoginEmail(origin) {
  return origin === 'EMAIL' || origin === 'GOOGLE';
}

/** 邮箱 / Google：平台 Session 已落盘，只做 type=2 登记，禁止再走 login/wallet */
function isCustodialPlatformOrigin(origin) {
  return origin === 'EMAIL' || origin === 'GOOGLE';
}

function resolveBindOrigin(originLoginType) {
  return normalizeOriginLoginType(originLoginType) || getOriginLoginType();
}

/** 把真实登录方式挂到 prepared，供手动授权 / pending 恢复复用 */
function withLoginOriginMeta(prepared, { inviteCode, originLoginType, email } = {}) {
  const origin = normalizeOriginLoginType(originLoginType || prepared?.originLoginType);
  const resolvedEmail =
    String(email ?? prepared?.email ?? '').trim() ||
    (shouldAttachLoginEmail(origin) ? extractPrivyEmail(prepared?.user) : '');

  return {
    ...prepared,
    inviteCode: inviteCode || prepared?.inviteCode || undefined,
    originLoginType: origin || undefined,
    email: shouldAttachLoginEmail(origin) && resolvedEmail ? resolvedEmail : undefined,
  };
}

/** 新钱包刚 create / addSigners 后，后端查 Privy 常会 422/超时，需多轮等待 */
const SIGNER_GRANTED_POLL_ATTEMPTS = 12;
const SIGNER_GRANTED_POLL_DELAY_MS = 1000;
const SIGNER_GRANTED_POST_GRANT_DELAY_MS = 800;

function isAppSignerStatusTransientError(err) {
  const code = Number(err?.code ?? err?.response?.status ?? NaN);
  if (code === 408 || code === 422 || code === 429 || code === 503 || code === 504) {
    return true;
  }
  const msg = String(err?.message ?? err ?? '').toLowerCase();
  return (
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('超时') ||
    msg.includes('422') ||
    msg.includes('授权失败') ||
    msg.includes('not ready') ||
    msg.includes('temporarily') ||
    msg.includes('try again')
  );
}

async function waitForAppSignerGranted(user) {
  let lastStatus = null;
  for (let attempt = 0; attempt < SIGNER_GRANTED_POLL_ATTEMPTS; attempt += 1) {
    lastStatus = await checkPrivyAppSignerGranted(user, { soft: true });
    if (lastStatus.granted) return lastStatus;
    if (attempt < SIGNER_GRANTED_POLL_ATTEMPTS - 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, SIGNER_GRANTED_POLL_DELAY_MS);
      });
    }
  }
  return lastStatus;
}

/**
 * 授权前查询：Privy App Signer 是否已 GRANTED
 * @param {object} [user]
 * @param {{ soft?: boolean }} [options] soft=true 时 422/超时视为未授权（不抛错），便于继续 addSigners
 */
export async function checkPrivyAppSignerGranted(user, { soft = false } = {}) {
  const currentUser = user?.id ? user : await getPrivySessionUser();
  if (!currentUser?.id) {
    throw new Error('Privy user is missing');
  }

  const { address, privyWalletId } = extractPrivyTradeAddressFields(currentUser);
  if (!address || !privyWalletId) {
    throw new Error('Privy embedded wallet is missing');
  }

  try {
    const data = await fetchPrivyAppSignerGranted({
      privyWalletId,
      userAddress: address,
    });

    const status = String(data?.status ?? '').trim();
    const granted = data?.granted === true || status === 'GRANTED';

    return {
      granted,
      status,
      message: String(data?.message ?? '').trim(),
      user: currentUser,
      address,
      privyWalletId,
    };
  } catch (err) {
    // 新创建托管钱包立刻查 granted：后端常 422「授权失败，超时」——应继续静默 addSigners
    if (soft || isAppSignerStatusTransientError(err)) {
      console.warn('[privy-bind] app-signer-granted transient, treat as not granted', err);
      return {
        granted: false,
        status: 'PENDING',
        message: String(err?.message ?? '').trim(),
        user: currentUser,
        address,
        privyWalletId,
        transient: true,
      };
    }
    throw err;
  }
}

/**
 * 平台 Session 下登记托管交易地址（type=2）。
 * 禁止再调 /login/wallet，否则会以嵌入式地址新建 WALLET 孤儿账户并覆盖 Session。
 */
async function finalizeCustodialTradeAddressBind({ user, address, privyWalletId, origin }) {
  if (!getAccessToken()) {
    throw new Error('Platform session required before custodial bind');
  }
  if (!address || !privyWalletId) {
    throw new Error('Privy embedded wallet is missing');
  }

  setWalletProviderId('privy');
  setWalletAddress(address);
  if (origin) {
    setOriginLoginType(origin);
    noteAnalyticsLoginMethod(origin);
  }
  setLoginWalletProviderId('');

  await registerPrivyTradeAddress(user);
  const { address: resolvedAddress, records } = await fetchTradeAddressFromList({
    embeddedAddress: address,
    privyWalletId,
  });
  if (!hasCustodialTradeAddress(records)) {
    throw new Error('Custodial trade address bind failed');
  }
  if (resolvedAddress) {
    setWalletAddress(resolvedAddress);
  } else {
    setWalletAddress(address);
  }

  const gasWallet = resolvedAddress || address;
  void postCustodialGasEnsure({ walletAddress: gasWallet }).catch((err) => {
    console.warn('[custodial-gas] post-bind ensure failed', err);
  });
  // 绑定 ≠ 通道 ready：异步静默 ensure-ready（gas + approve + CTF）
  warmPrivyTradingReadyAfterBind(gasWallet);

  return {
    bound: true,
    address: resolvedAddress || address,
    privyWalletId,
  };
}

/**
 * 准备托管绑定上下文。邮箱/Google（已有平台 Session）不生成 login/wallet 验签材料。
 */
export async function preparePrivyBackendBind(user, { originLoginType } = {}) {
  if (!user?.id) {
    throw new Error('Privy user is missing');
  }

  const freshUser = await getPrivySessionUser();
  const currentUser = freshUser?.id ? freshUser : user;

  const { address, privyWalletId } = extractPrivyTradeAddressFields(currentUser);
  if (!address || !privyWalletId) {
    throw new Error('Privy embedded wallet is missing');
  }

  const origin = resolveBindOrigin(originLoginType);
  // 邮箱/Google 主路径：平台 JWT 已在，只需 address + privyWalletId 做 trade-address/add
  if (isCustodialPlatformOrigin(origin) && getAccessToken()) {
    return {
      user: currentUser,
      address,
      privyWalletId,
      walletAddress: address,
      signature: '',
      message: '',
      skipWalletLogin: true,
    };
  }

  const { walletAddress, signature, message } = await createPrivyWalletLoginCredentials(
    currentUser
  );

  return {
    user: currentUser,
    address,
    privyWalletId,
    walletAddress,
    signature,
    message,
    skipWalletLogin: false,
  };
}

/**
 * Privy sync 后：先查 app-signer-granted，已授权则直接完成托管登记。
 */
export async function resolvePrivyBackendBindAfterLogin(
  user,
  { inviteCode, originLoginType, email } = {}
) {
  const prepared = withLoginOriginMeta(await preparePrivyBackendBind(user, { originLoginType }), {
    inviteCode,
    originLoginType,
    email,
  });
  // soft：新建钱包立刻查常 422/超时，勿阻断后续静默授权
  const signerStatus = await checkPrivyAppSignerGranted(prepared.user, { soft: true });

  if (signerStatus.granted) {
    const data = await submitPrivyBackendBind(prepared);
    return { autoLoggedIn: true, data, prepared, signerStatus };
  }

  return {
    autoLoggedIn: false,
    prepared,
    signerStatus,
  };
}

/**
 * Privy sync 后：已授权则直接 bind；未授权则自动 addSigners 并完成 bind。
 * 自动授权失败时，错误对象会附带 bindResult 供 fallback 手动授权。
 */
export async function completePrivyBackendBindAfterLogin(
  user,
  { inviteCode, originLoginType, email } = {}
) {
  const bindResult = await resolvePrivyBackendBindAfterLogin(user, {
    inviteCode,
    originLoginType,
    email,
  });
  if (bindResult.autoLoggedIn) {
    return bindResult;
  }

  try {
    const data = await confirmPrivyBackendBind(bindResult.prepared);
    return { ...bindResult, autoLoggedIn: true, data };
  } catch (err) {
    err.bindResult = bindResult;
    throw err;
  }
}

/** Privy addSigners（静默）→ 尽量等后端 granted → 托管 trade-address 登记 */
export async function confirmPrivyBackendBind(
  prepared,
  { inviteCode, originLoginType, email } = {}
) {
  const withMeta = withLoginOriginMeta(prepared, { inviteCode, originLoginType, email });
  if (!withMeta?.user?.id) {
    throw new Error('Privy wallet bind context is missing');
  }

  // 程序静默挂 App Signer；已存在 / Duplicate 也会 skipped=true
  const grantResult = await grantPrivyAppSigner(withMeta.user);
  const user = grantResult.user ?? withMeta.user;

  // Privy 侧已成功时，后端 app-signer-granted 常仍 422/超时——不应阻断登录或弹手动授权
  if (grantResult.skipped) {
    // 已挂过 signer：轻量再查一次，失败也继续 bind
    const quickStatus = await checkPrivyAppSignerGranted(user, { soft: true });
    if (!quickStatus.granted) {
      console.warn(
        '[privy-bind] signer already on Privy wallet; backend granted pending, proceed',
        quickStatus.message
      );
    }
  } else {
    await new Promise((resolve) => {
      setTimeout(resolve, SIGNER_GRANTED_POST_GRANT_DELAY_MS);
    });
    const signerStatus = await waitForAppSignerGranted(user);
    if (!signerStatus?.granted) {
      console.warn(
        '[privy-bind] app-signer-granted not ready after Privy grant; proceed with bind',
        {
          status: signerStatus?.status,
          message: signerStatus?.message,
        }
      );
    }
  }

  return submitPrivyBackendBind({
    ...withMeta,
    user,
  });
}

/**
 * 完成托管绑定。
 * - EMAIL / GOOGLE：保留平台 Session，仅 trade-address/add type=2
 * - 其它遗留路径：仍可走 bindPrivyAccount（login/wallet addressType=2）
 */
export async function submitPrivyBackendBind({
  user,
  address,
  privyWalletId,
  walletAddress,
  signature,
  message,
  inviteCode,
  originLoginType,
  email,
} = {}) {
  if (!user?.id) {
    throw new Error('Privy user is missing');
  }
  if (!address || !privyWalletId) {
    throw new Error('Privy embedded wallet is missing');
  }

  const origin = resolveBindOrigin(originLoginType);

  if (isCustodialPlatformOrigin(origin)) {
    return finalizeCustodialTradeAddressBind({
      user,
      address,
      privyWalletId,
      origin,
    });
  }

  if (!walletAddress || !signature || !message) {
    throw new Error('Privy wallet login credentials are missing');
  }

  // 遗留路径：非 EMAIL/GOOGLE 才可能走到此处；禁止再带邮箱轨 origin
  const data = await bindPrivyAccount({
    address,
    privyWalletId,
    walletAddress,
    signature,
    message,
    inviteCode: inviteCode || undefined,
    originLoginType: origin || 'WALLET',
  });

  setWalletProviderId('privy');
  setWalletAddress(address);
  if (origin) {
    setOriginLoginType(origin);
    noteAnalyticsLoginMethod(origin);
  }
  if (origin !== 'WALLET') {
    setLoginWalletProviderId('');
  }

  const hasToken =
    data &&
    typeof data === 'object' &&
    (data.accessToken || data.token || data.access_token || data.jwt);

  if (hasToken) {
    persistLoginResponse(data);
  } else if (data === true) {
    throw new Error('Login succeeded but no access token received');
  } else if (data && typeof data === 'object') {
    persistLoginResponse(data);
  }

  if (!getAccessToken()) {
    throw new Error('Login succeeded but no access token received');
  }

  setWalletAddress(address);
  const fromLogin =
    data && typeof data === 'object'
      ? data.walletAddress ?? data.wallet ?? data.address ?? data.ethAddress ?? null
      : null;
  if (fromLogin && addressesEqual(fromLogin, address)) {
    setWalletAddress(normalizeAddress(fromLogin));
  }

  await registerPrivyTradeAddress(user);
  const { address: resolvedAddress, records } = await fetchTradeAddressFromList({
    embeddedAddress: address,
    privyWalletId,
  });
  if (!hasCustodialTradeAddress(records)) {
    throw new Error('Custodial trade address bind failed');
  }
  if (resolvedAddress) {
    setWalletAddress(resolvedAddress);
  } else {
    setWalletAddress(address);
  }

  const gasWallet = resolvedAddress || address;
  void postCustodialGasEnsure({ walletAddress: gasWallet }).catch((err) => {
    console.warn('[custodial-gas] post-bind ensure failed', err);
  });
  // 绑定 ≠ 通道 ready：异步静默 ensure-ready（gas + approve + CTF）
  warmPrivyTradingReadyAfterBind(gasWallet);

  return data;
}

/** @deprecated 请使用 preparePrivyBackendBind + submitPrivyBackendBind */
export async function completePrivyBackendBind({ user, inviteCode, originLoginType, email } = {}) {
  const prepared = withLoginOriginMeta(await preparePrivyBackendBind(user, { originLoginType }), {
    inviteCode,
    originLoginType,
    email,
  });
  return submitPrivyBackendBind(prepared);
}
